import React, { useEffect, useRef } from 'react';
import { X } from '@phosphor-icons/react';
interface NoMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerm: string;
  onSelectTopic?: (topic: string) => void;
}
export function NoMatchModal({
  isOpen,
  onClose,
  searchTerm,
  onSelectTopic
}: NoMatchModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      console.log('NoMatchModal opened with search term:', searchTerm);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, searchTerm]);
  if (!isOpen) return null;
  // Determine the context of the search to provide better suggestions
  const getContextualSuggestions = () => {
    const term = searchTerm.toLowerCase();
    // Check for landscape/design related terms
    if (term.includes('landscape') || term.includes('garden') || term.includes('outdoor')) {
      return [{
        text: 'UI Design',
        description: 'Visual design work for digital interfaces'
      }, {
        text: 'Conversational AI',
        description: 'Experience with chatbots and voice interfaces'
      }, {
        text: 'US Bank case study',
        description: 'Financial services design work'
      }, {
        text: 'Cybersecurity',
        description: 'Experience designing security interfaces'
      }, {
        text: 'Higher education',
        description: 'Experience working with universities and educational institutions'
      }];
    }
    // Check for retail/e-commerce related terms
    if (term.includes('retail') || term.includes('shop') || term.includes('store') || term.includes('e-commerce')) {
      return [{
        text: 'Financial services',
        description: 'Work in the banking and financial sector'
      }, {
        text: 'US Bank case study',
        description: 'Corporate card application design'
      }, {
        text: 'User experience design',
        description: 'Creating intuitive digital experiences'
      }, {
        text: 'Conversational AI',
        description: 'Experience with chatbots and voice interfaces'
      }, {
        text: 'Higher education',
        description: 'Experience working with universities and educational institutions'
      }];
    }
    // Default suggestions - limited to 5
    return [{
      text: 'User interviews',
      description: 'Experience conducting user research interviews'
    }, {
      text: 'Conversational AI',
      description: 'Experience with chatbots and voice interfaces'
    }, {
      text: 'Financial services',
      description: 'Work in the banking and financial sector'
    }, {
      text: 'Cybersecurity',
      description: 'Experience designing security interfaces'
    }, {
      text: 'Higher education',
      description: 'Experience working with universities and educational institutions'
    }];
  };
  const suggestedTopics = getContextualSuggestions().slice(0, 5); // Ensure only 5 topics max
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-383">
      <div ref={modalRef} className="bg-black rounded-lg shadow-xl max-w-md w-full border border-white/20 overflow-hidden" data-id="element-384">
        <div className="p-5 border-b border-white/20 flex justify-between items-center" data-id="element-385">
          <h3 className="text-lg font-medium text-white" data-id="element-386">No Match Found</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors" aria-label="Close modal" data-id="element-387">
            <X size={20} data-id="element-388" />
          </button>
        </div>
        <div className="p-5" data-id="element-389">
          <div className="bg-white/5 border border-white/20 rounded-lg p-4 mb-5" data-id="element-390">
            <p className="text-white" data-id="element-391">
              Sorry, I don't have specific experience with{' '}
              <span className="font-semibold" data-id="element-392">"{searchTerm}"</span>.
            </p>
          </div>
          <p className="text-white/80 mb-4" data-id="element-393">
            Try asking about these areas instead:
          </p>
          <div className="space-y-3 mb-5" data-id="element-394">
            {suggestedTopics.map((topic, index) => <button key={index} onClick={() => {
            if (onSelectTopic) {
              onSelectTopic(topic.text);
            }
            onClose();
          }} className="w-full bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg p-3 text-left transition-colors" data-id="element-395">
                <p className="text-white font-medium" data-id="element-396">{topic.text}</p>
                <p className="text-white/60 text-sm" data-id="element-397">{topic.description}</p>
              </button>)}
          </div>
          <div className="flex justify-end items-center pt-4 border-t border-white/20" data-id="element-398">
            <button onClick={onClose} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md transition-colors text-sm" data-id="element-399">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
}